import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';

import api from '../../services/api';
import './profile.css';
import ProfileFormFields from './profile-form-fields';
import { UserProfileDetailsProps } from './profile.types';

const serializeBeneficiaries = (beneficiaries: any) => {
  return beneficiaries.map((beneficiary: any) => {
    const { beneficiaryProfile, beneficiaryDocuments, beneficiaryBanks, profileBeneficiaryId } =
      beneficiary;
    return {
      ...beneficiaryProfile,
      profileBeneficiaryId,
      ...beneficiaryBanks,
      documents: Array.isArray(beneficiaryDocuments)
        ? beneficiaryDocuments
        : [beneficiaryDocuments],
    };
  });
};

const ProfileDetails: React.FC<UserProfileDetailsProps> = (props: UserProfileDetailsProps) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>({});
  const [userId, setUserId] = useState<number | null>();
  const [fieldsToUpdate, setFieldsToUpdate] = useState<any>({});
  const [selectedBeneficiaryIndex, setSelectedBeneficiaryIndex] = useState<number | null>(null);

  const serializeProfile = useCallback((profile: any) => {
    const { beneficiaries, banks, ...profileData } = profile;
    const parsedBeneficiaries = beneficiaries && serializeBeneficiaries(beneficiaries);

    return {
      ...profileData,
      bankId: banks && banks[0]?.bankId,
      clabe: banks && banks[0].clabe,
      bankName: banks && banks[0].currentBank.name,
      beneficiaries: parsedBeneficiaries,
    };
  }, []);

  useEffect(() => {
    const { userId, profile } = props.userProfile;
    if (userId) {
      setProfile(serializeProfile(profile));
      setUserId(userId);
    }
  }, [props]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { beneficiaries, ...customerData } = fieldsToUpdate;
      if (customerData.phone) {
        customerData.phone = customerData.phone.replace(new RegExp('-', 'g'), () => '');
      }
      await api.patch(`/users/${userId}/profile`, customerData);
      alert('Cliente actualizado correctamente');
    } catch (error) {
      console.error('Error al actualizar el cliente:', error);
      alert('Ocurrió un error al actualizar datos del cliente.');
    }
  };

  const validateBeneficiaryDataUpdated = (beneficiaryData: any) => {
    const { documents, ...beneficiary } = beneficiaryData;
    const validatedDocuments = documents.filter((doc: any) => doc.file);

    return {
      ...beneficiary,
      documents: validatedDocuments,
    };
  };

  const handleSubmitBeneficiary = async () => {
    const { beneficiaries } = fieldsToUpdate;
    let beneficiaryData;
    if (selectedBeneficiaryIndex !== null) {
      beneficiaryData = beneficiaries[selectedBeneficiaryIndex];
    }
    try {
      if (beneficiaryData.profileBeneficiaryId) {
        beneficiaryData = validateBeneficiaryDataUpdated(beneficiaryData);
        await api.patch(
          `/users/${userId}/beneficiaries/${beneficiaryData.profileBeneficiaryId}`,
          beneficiaryData,
        );
      } else {
        await api.post(`/users/${userId}/beneficiaries`, { beneficiaries: [beneficiaryData] });
      }
    } catch (error) {
      alert('Ocurrió un error en el proceso de beneficiarios.');
    }
  };

  const handleChange = (e: React.ChangeEvent<any>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    if (selectedBeneficiaryIndex !== null) {
      // Asegurar que beneficiaries existe
      const updatedBeneficiaries = Array.isArray(fieldsToUpdate.beneficiaries)
        ? [...fieldsToUpdate.beneficiaries]
        : [...profile.beneficiaries];

      if (!updatedBeneficiaries[selectedBeneficiaryIndex]) {
        updatedBeneficiaries[selectedBeneficiaryIndex] = {};
      }

      // Actualizar el beneficiario
      updatedBeneficiaries[selectedBeneficiaryIndex] = {
        ...updatedBeneficiaries[selectedBeneficiaryIndex],
        [target.name]: target.value,
      };
      profile.beneficiaries[selectedBeneficiaryIndex] =
        updatedBeneficiaries[selectedBeneficiaryIndex];

      setProfile(profile);
      setFieldsToUpdate({ ...fieldsToUpdate, beneficiaries: updatedBeneficiaries });
    } else {
      setProfile({ ...profile, [target.name]: target.value });
      setFieldsToUpdate({ ...fieldsToUpdate, [target.name]: target.value });
    }
  };

  const handleFileChange = (
    e: React.ChangeEvent<any>,
    documentTypeId: number,
    index: number | null,
  ) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      const file = files[0];

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        const base64String = (reader.result as string).split(',')[1];
        setFieldsToUpdate((prevData: any) => {
          if (index !== null) {
            const updatedBeneficiaries = Array.isArray(fieldsToUpdate?.beneficiaries)
              ? [...fieldsToUpdate.beneficiaries]
              : [];

            if (!updatedBeneficiaries[index]) {
              const profileBeneficiaryId = profile.beneficiaries[index].profileBeneficiaryId;
              updatedBeneficiaries[index] = { profileBeneficiaryId, documents: [] };
            }

            const currentBeneficiary = updatedBeneficiaries[index];

            updatedBeneficiaries[index] = {
              ...currentBeneficiary,
              documents: Array.isArray(currentBeneficiary?.documents)
                ? [
                    ...currentBeneficiary.documents.filter(
                      (doc: any) => doc.documentTypeId !== documentTypeId,
                    ),
                    {
                      documentTypeId,
                      filename: file.name,
                      file: base64String,
                    },
                  ]
                : [{ documentTypeId, filename: file.name, file: base64String }],
            };

            return {
              ...prevData,
              beneficiaries: updatedBeneficiaries,
            };
          } else {
            return {
              ...prevData,
              documents: [
                ...(prevData?.documents ?? []).filter(
                  (doc: any) => doc.documentTypeId !== documentTypeId,
                ),
                {
                  documentTypeId,
                  filename: file.name,
                  file: base64String,
                },
              ],
            };
          }
        });
      };
    }
  };

  return (
    <Container>
      <Form onSubmit={handleSubmit}>
        <Row className="mt-3 sticky-header">
          <Col md={10}>
            <Button variant="secondary" onClick={() => navigate('/dashboard')}>
              Regresar
            </Button>
          </Col>
          <Col md={2}>
            <Button variant="primary" onClick={handleSubmit}>
              Guardar
            </Button>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col className="text-left" md={9}>
            La siguiente información nos permitirá verificar tu perfil como inversionista. Al tener
            los datos completos y correctos podremos depositar tus rendimientos en tiempo y forma,
            así como poder emitir las constancias de retenciones como marca la Ley.
          </Col>
          <Col md={3}>
            <Form.Group as={Row} className="mb-1">
              <Form.Label className="fw-bold">Perfil Seleccionado</Form.Label>
              <Form.Control
                type="text"
                name="perfilSeleccionado"
                value="Persona física"
                width="10"
                disabled
              />
              <Form.Label className="fw-bold">Correo electrónico</Form.Label>
              <Form.Control type="text" name="firstName" value={profile?.email} disabled />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-4">
          {
            <ProfileFormFields
              formData={profile}
              handleChange={handleChange}
              handleFileChange={handleFileChange}
              handleBeneficiarySubmit={handleSubmitBeneficiary}
              isEdit={true}
              setSelectedBeneficiaryIndex={setSelectedBeneficiaryIndex}
            />
          }
        </Row>
      </Form>
    </Container>
  );
};

export default ProfileDetails;
